'use client';

import React from 'react';
import { motion } from 'framer-motion';

import { Box, BoxProps } from '../box';
import { SimpleGrid } from '../simple-grid';

import { DetailInfoField, DetailInfoFieldProps } from './detail-info-field';

// Motion div for animations (using native div to avoid Chakra prop conflicts)
const MotionDiv = motion.div;

export interface InfoGridField extends Omit<DetailInfoFieldProps, 'staggerDelay'> {
  /** Unique key for the field */
  key?: string;
  /** Span multiple columns */
  colSpan?: number;
}

export interface DetailInfoGridProps extends Omit<BoxProps, 'children'> {
  /** Array of field configurations */
  fields: InfoGridField[];
  /** Number of columns */
  columns?: number | { base?: number; sm?: number; md?: number; lg?: number; xl?: number };
  /** Gap between fields */
  gap?: number | string;
  /** Show dividers between fields */
  showDividers?: boolean;
  /** Field layout */
  fieldLayout?: 'vertical' | 'horizontal';
  /** Field size */
  fieldSize?: 'sm' | 'md' | 'lg';
  /** Loading state */
  isLoading?: boolean;
  /** Animation stagger delay in seconds */
  staggerDelay?: number;
}

/**
 * DetailInfoGrid - Grid layout for multiple info fields
 * Features staggered animations and responsive columns
 */
export const DetailInfoGrid: React.FC<DetailInfoGridProps> = ({
  fields,
  columns = { base: 1, sm: 2, lg: 3 },
  gap = 5,
  showDividers = false,
  fieldLayout = 'vertical',
  fieldSize = 'md',
  isLoading = false,
  staggerDelay = 0.05,
  ...boxProps
}) => {
  // Filter out fields with hideIfEmpty and empty values
  const visibleFields = fields.filter((field) => {
    if (!field.hideIfEmpty) return true;
    const isEmpty =
      field.value === undefined ||
      field.value === null ||
      field.value === '' ||
      (typeof field.value === 'string' && field.value.trim() === '');
    return !isEmpty;
  });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDelay,
        delayChildren: 0.1,
      },
    },
  };

  return (
    <Box {...boxProps}>
      <MotionDiv variants={containerVariants} initial="hidden" animate="visible">
        <SimpleGrid columns={columns} gap={gap}>
          {visibleFields.map((field, index) => {
            const { key, colSpan, label, ...restFieldProps } = field;
            const fieldKey = key || `field-${index}`;

            return (
              <Box
                key={fieldKey}
                gridColumn={colSpan ? `span ${colSpan}` : undefined}
                position="relative"
                paddingBottom={showDividers ? 4 : 0}
                _after={
                  showDividers
                    ? {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '1px',
                        backgroundColor: 'border.muted',
                      }
                    : undefined
                }
              >
                <DetailInfoField
                  label={label}
                  {...restFieldProps}
                  layout={restFieldProps.layout || fieldLayout}
                  size={restFieldProps.size || fieldSize}
                  isLoading={isLoading}
                  staggerDelay={index * staggerDelay}
                />
              </Box>
            );
          })}
        </SimpleGrid>
      </MotionDiv>
    </Box>
  );
};

export default DetailInfoGrid;
